import type { ReactNode } from 'react'
import { Helmet } from 'react-helmet'
import { Link } from 'react-router-dom'

import styles from './PublicSite.module.css'

const supportEmail = 'support@logreplay.com'

const pricingPlans = [
	{
		name: 'Free',
		price: '$0',
		description: 'For validating replay on one production website.',
		features: [
			'1 website',
			'1,000 sessions per month',
			'100 sessions per day',
			'7 day data retention',
		],
	},
	{
		name: 'Starter',
		price: '$19',
		description: 'For small teams that need replay and issue context.',
		features: [
			'3 websites',
			'10,000 sessions per month',
			'1,000 sessions per day',
			'30 day data retention',
		],
	},
	{
		name: 'Growth',
		price: '$79',
		description: 'For growing products with more sessions to review.',
		features: [
			'10 websites',
			'75,000 sessions per month',
			'7,500 sessions per day',
			'90 day data retention',
		],
	},
	{
		name: 'Business',
		price: '$249',
		description: 'For teams that rely on replay across support and engineering.',
		features: [
			'25 websites',
			'250,000 sessions per month',
			'25,000 sessions per day',
			'180 day data retention',
		],
	},
	{
		name: 'Enterprise',
		price: 'Custom',
		description: 'For larger teams with custom volume, retention, or rollout needs.',
		features: [
			'Custom retention and volume',
			'SSO and access controls',
			'On-premise deployment support',
			'Priority implementation support',
		],
	},
] as const

const features = [
	{
		title: 'Watch the issue unfold',
		body: 'Replay real browser sessions with clicks, page changes, and user activity on the timeline so you can see the moment confusion or failure starts.',
		image: '/review-site/session-replay.png',
		alt: 'LogReplay session replay interface with event timeline',
	},
	{
		title: 'Debug with technical evidence',
		body: 'Line up replay with console logs, exceptions, network requests, backend traces, and metadata from the same customer session.',
		image: '/review-site/logging.webp',
		alt: 'LogReplay log search interface',
	},
	{
		title: 'Prioritize what matters',
		body: 'Use dashboards for errors, performance, sessions, and product health to spot recurring problems instead of chasing one-off reports.',
		image: '/review-site/dashboards.webp',
		alt: 'LogReplay dashboard charts for frontend performance',
	},
] as const

const faqs = [
	{
		question: 'What does LogReplay sell?',
		answer: 'LogReplay is a software-as-a-service product for session replay, error monitoring, logs, traces, metrics, and product analytics for web applications.',
	},
	{
		question: 'How do customers install it?',
		answer: 'Customers add a browser SDK to their website, create a project, and send replay and monitoring data to their LogReplay workspace.',
	},
	{
		question: 'How is billing handled?',
		answer: 'Plans are billed monthly. Paid tiers are based on website count, monthly session volume, daily session limits, and retention.',
	},
	{
		question: 'How can customers get help?',
		answer: `Customers can contact ${supportEmail} for product support, billing questions, account access, and refund requests.`,
	},
] as const

const PublicHead = ({
	title,
	description,
	path = '/',
}: {
	title: string
	description: string
	path?: string
}) => {
	const canonical = `https://logreplay.com${path}`

	return (
		<Helmet>
			<title>{title}</title>
			<meta name="title" content={title} />
			<meta name="description" content={description} />
			<link rel="canonical" href={canonical} />
			<meta property="og:title" content={title} />
			<meta property="og:description" content={description} />
			<meta property="og:url" content={canonical} />
			<meta property="og:type" content="website" />
			<meta name="twitter:card" content="summary_large_image" />
			<meta name="twitter:title" content={title} />
			<meta name="twitter:description" content={description} />
		</Helmet>
	)
}

const PublicNav = () => (
	<header className={styles.nav}>
		<Link className={styles.brand} to="/" aria-label="LogReplay home">
			<span className={styles.brandMark}>L</span>
			<span>LogReplay</span>
		</Link>
		<nav className={styles.navLinks} aria-label="Primary navigation">
			<Link to="/pricing">Pricing</Link>
			<a href="/blog/">Blog</a>
			<Link to="/contact">Contact</Link>
			<a href="/terms">Terms</a>
			<a href="/privacy">Privacy</a>
			<Link className={styles.navButton} to="/sign_in">
				Sign in
			</Link>
		</nav>
	</header>
)

const PublicFooter = () => (
	<footer className={styles.footer}>
		<div>
			<Link className={styles.footerBrand} to="/">
				LogReplay
			</Link>
			<p>
				Session replay with the logs, errors, traces, and metrics teams
				need to fix production issues faster.
			</p>
		</div>
		<div className={styles.footerLinks}>
			<Link to="/pricing">Pricing</Link>
			<a href="/blog/">Blog</a>
			<Link to="/contact">Contact</Link>
			<a href="/terms">Terms of Service</a>
			<a href="/privacy">Privacy Policy</a>
			<a href="/refund">Refund Policy</a>
			<a href={`mailto:${supportEmail}`}>{supportEmail}</a>
		</div>
	</footer>
)

const PageShell = ({ children }: { children: ReactNode }) => (
	<div className={styles.page}>
		<PublicNav />
		{children}
		<PublicFooter />
	</div>
)

export const PublicHome = () => {
	return (
		<PageShell>
			<PublicHead
				title="LogReplay - Session replay for production debugging"
				description="LogReplay helps teams replay real user sessions with logs, errors, traces, metrics, and product context."
			/>
			<main>
				<section className={styles.hero}>
					<div className={styles.heroCopy}>
						<p className={styles.eyebrow}>
							Session replay for production teams
						</p>
						<h1>Replay the exact session behind every bug.</h1>
						<p className={styles.lede}>
							LogReplay records real browser sessions and lines
							them up with console logs, network requests, errors,
							traces, and metrics, so your team can move from
							"what happened?" to a fix without another
							screenshot.
						</p>
						<div className={styles.heroActions}>
							<Link className={styles.primaryCta} to="/sign_up">
								Get started
							</Link>
							<Link className={styles.secondaryCta} to="/pricing">
								View pricing
							</Link>
						</div>
						<ul className={styles.trustList}>
							<li>Replay, logs, errors, traces, and metrics</li>
							<li>HTTPS-secured product and public policies</li>
							<li>Support at {supportEmail}</li>
						</ul>
					</div>
					<div className={styles.heroProduct} aria-label="Product preview">
						<div className={styles.productFrame}>
							<div className={styles.productTopBar}>
								<span />
								<span />
								<span />
								<strong>Live session</strong>
							</div>
							<img
								src="/review-site/session-replay.png"
								alt="LogReplay product preview showing session replay timeline"
							/>
						</div>
					</div>
				</section>

				<section className={styles.metricsBand} aria-label="Product scope">
					<div>
						<strong>Replay</strong>
						<span>Real sessions, clicks, and page flow</span>
					</div>
					<div>
						<strong>Diagnose</strong>
						<span>Errors, console logs, and network requests</span>
					</div>
					<div>
						<strong>Resolve</strong>
						<span>Shareable context for support and engineering</span>
					</div>
				</section>

				<section className={styles.section}>
					<div className={styles.sectionHeader}>
						<p className={styles.eyebrow}>Product</p>
						<h2>The replay is only the start.</h2>
						<p>
							Session replay shows what the customer experienced.
							LogReplay adds the technical context your team needs
							to understand why it happened.
						</p>
					</div>
					<div className={styles.featureGrid}>
						{features.map((feature) => (
							<article className={styles.featureCard} key={feature.title}>
								<img src={feature.image} alt={feature.alt} />
								<div>
									<h3>{feature.title}</h3>
									<p>{feature.body}</p>
								</div>
							</article>
						))}
					</div>
				</section>

				<section className={styles.setupSection}>
					<div>
						<p className={styles.eyebrow}>Install</p>
						<h2>Install once. Debug the next real session.</h2>
						<p>
							Create a project, add the browser recorder, and start
							collecting the replay and monitoring data your team
							needs when production issues appear.
						</p>
					</div>
					<pre className={styles.codeBlock}>
						<code>{`<script src="https://logreplay.com/static/sdk/logreplay-recorder.umd.js"></script>
<script>
  window.H.init('YOUR_PROJECT_ID', {
    backendUrl: 'https://logreplay.com/public',
  })
</script>`}</code>
					</pre>
				</section>
			</main>
		</PageShell>
	)
}

export const PublicPricing = () => {
	return (
		<PageShell>
			<PublicHead
				title="LogReplay pricing"
				description="Monthly LogReplay plans for session replay, monitoring, logs, traces, metrics, and product analytics."
				path="/pricing"
			/>
			<main>
				<section className={styles.subHero}>
					<p className={styles.eyebrow}>Pricing</p>
					<h1>Simple plans for production replay volume.</h1>
					<p>
						Start with one website, then scale by session volume,
						daily capture limits, retention, and the number of
						properties your team needs to monitor.
					</p>
				</section>
				<section className={styles.pricingGrid} aria-label="Pricing plans">
					{pricingPlans.map((plan) => (
						<article className={styles.priceCard} key={plan.name}>
							<div>
								<h2>{plan.name}</h2>
								<p>{plan.description}</p>
							</div>
							<div className={styles.price}>
								<span>{plan.price}</span>
								{plan.price !== 'Custom' && <small>/ month</small>}
							</div>
							<ul>
								{plan.features.map((feature) => (
									<li key={feature}>{feature}</li>
								))}
							</ul>
							<Link
								className={
									plan.name === 'Enterprise'
										? styles.secondaryCta
										: styles.primaryCta
								}
								to={plan.name === 'Enterprise' ? '/contact' : '/sign_up'}
							>
								{plan.name === 'Enterprise'
									? 'Contact sales'
									: 'Get started'}
							</Link>
						</article>
					))}
				</section>
				<section className={styles.faqGrid} aria-label="Pricing questions">
					{faqs.map((faq) => (
						<article key={faq.question}>
							<h3>{faq.question}</h3>
							<p>{faq.answer}</p>
						</article>
					))}
				</section>
			</main>
		</PageShell>
	)
}

export const PublicContact = () => {
	return (
		<PageShell>
			<PublicHead
				title="Contact LogReplay"
				description="Contact LogReplay for support, sales, billing, refunds, and product questions."
				path="/contact"
			/>
			<main>
				<section className={styles.contactHero}>
					<div>
						<p className={styles.eyebrow}>Contact</p>
						<h1>Talk to someone who can help.</h1>
						<p>
							Reach out for product questions, implementation help,
							account access, billing, sales, or refund requests.
							We will route your message to the right person.
						</p>
						<a className={styles.primaryCta} href={`mailto:${supportEmail}`}>
							Email support
						</a>
					</div>
					<div className={styles.contactPanel}>
						<h2>Company and product details</h2>
						<dl>
							<div>
								<dt>Product</dt>
								<dd>LogReplay</dd>
							</div>
							<div>
								<dt>Website</dt>
								<dd>https://logreplay.com</dd>
							</div>
							<div>
								<dt>Support email</dt>
								<dd>
									<a href={`mailto:${supportEmail}`}>
										{supportEmail}
									</a>
								</dd>
							</div>
							<div>
								<dt>Product category</dt>
								<dd>
									Session replay, monitoring, and product
									analytics software
								</dd>
							</div>
						</dl>
					</div>
				</section>
				<section className={styles.contactLinks}>
					<a href="/terms">Terms of Service</a>
					<a href="/privacy">Privacy Policy</a>
					<a href="/refund">Refund Policy</a>
					<Link to="/pricing">Pricing</Link>
				</section>
			</main>
		</PageShell>
	)
}

export const PublicPolicyBridge = ({
	title,
	description,
	path,
}: {
	title: string
	description: string
	path: string
}) => {
	return (
		<PageShell>
			<PublicHead title={title} description={description} path={path} />
			<main>
				<section className={styles.policyBridge}>
					<p className={styles.eyebrow}>Policy</p>
					<h1>{title}</h1>
					<p>{description}</p>
					<a className={styles.primaryCta} href={`${path}/`}>
						Open full policy
					</a>
				</section>
			</main>
		</PageShell>
	)
}
